import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { logWarn } from "@/lib/log";

const POLL_MS = 30_000;

/**
 * Encapsulates all notification state — unread count polling, list fetching,
 * and mark-all-read. Returns primitives so the UI stays presentational.
 */
export function useNotifications(user) {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const alive = useRef(true);

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    if (!user) { setCount(0); setItems([]); return; }
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (alive.current) setCount(data.count);
      } catch (err) {
        logWarn("[notif] unread-count poll failed", err?.response?.status);
      }
    };
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [user]);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=15");
      setItems(data.notifications);
    } catch (err) {
      logWarn("[notif] fetch failed", err?.response?.status);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setCount(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      logWarn("[notif] mark-all-read failed", err?.response?.status);
    }
  }, []);

  return { count, items, loadItems, markAllRead };
}
